import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Phone, ChatCircleDots, ArrowRight, Waveform, Lightning, DotsThree, ArrowUpRight, CircleNotch, CheckCircle, Users, Timer } from "@phosphor-icons/react";
import BuzzLogo from "../components/BuzzLogo";

/* ═══════════════════════ LIVE TICKER — shared, "app is running" feel ═══════════════════════ */
const TICKER_EVENTS = [
  { name: "Party of 4 · Chen",    action: "added by Mimi",   time: "12s",  status: "queued"  },
  { name: "Party of 2 · Alvarez", action: "notified · SMS",  time: "34s",  status: "notify"  },
  { name: "Inbound call",         action: "Ring 2 → Mimi",   time: "1m",   status: "live"    },
  { name: "Party of 6 · Kim",     action: "seated · T-12",   time: "2m",   status: "seated"  },
  { name: "SMS: 'On our way'",    action: "auto-reply sent", time: "3m",   status: "notify"  },
  { name: "Party of 3 · Patel",   action: "added by Mimi",   time: "4m",   status: "queued"  },
];
const useTicker = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI(v => (v + 1) % TICKER_EVENTS.length), 2200);
    return () => clearInterval(t);
  }, []);
  return TICKER_EVENTS[i];
};
const statusStyle = (s) => ({
  queued:  "bg-[#F5C543] text-[#121914]",
  notify:  "bg-[#C2D1C1] text-[#1E3A2F]",
  live:    "bg-[#D96C5B] text-white",
  seated:  "bg-[#1E3A2F] text-[#F5C543]",
}[s]);

/* Live signal dot — animated pulse */
const LiveDot = ({ color = "#F5C543" }) => (
  <span className="relative inline-flex w-2 h-2">
    <span className="absolute inset-0 rounded-full animate-ping opacity-75" style={{background: color}} />
    <span className="relative w-2 h-2 rounded-full" style={{background: color}} />
  </span>
);

/* Compact ticker strip — sits under headline in every option */
const TickerStrip = ({ dark = false }) => {
  const e = useTicker();
  return (
    <div className={`inline-flex items-center gap-3 text-xs font-mono ${dark ? "text-[#C2D1C1]" : "text-[#5C6B61]"}`}>
      <LiveDot color={dark ? "#F5C543" : "#D96C5B"} />
      <span className={`tracking-[0.2em] uppercase ${dark ? "text-[#F5C543]" : "text-[#D96C5B]"}`}>Live</span>
      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${statusStyle(e.status)}`}>{e.status}</span>
      <span className={dark ? "text-white" : "text-[#121914]"}>{e.name}</span>
      <span className="opacity-60">— {e.action}</span>
      <span className="opacity-40">· {e.time} ago</span>
    </div>
  );
};

const Label = ({ n, name, sub }) => (
  <div className="max-w-7xl mx-auto px-6 pt-14 pb-4 flex items-end justify-between border-b border-black/5">
    <div>
      <div className="text-[10px] tracking-[0.32em] uppercase text-[#5C6B61] font-semibold">Direction {n}</div>
      <h4 className="font-heading text-2xl mt-1 text-[#121914]">{name}</h4>
      <p className="text-xs text-[#5C6B61] mt-1 max-w-lg leading-relaxed">{sub}</p>
    </div>
    <div className="hidden md:block text-[10px] text-[#5C6B61] italic">Preview only · not linked from live nav</div>
  </div>
);

/* ═══════════════════════ D — LIVE TERMINAL (Linear × Bloomberg) ═══════════════════════ */
const HeroD = () => (
  <section id="opt-d" data-testid="hero-opt-d" className="bg-[#0E1512] text-[#F7F6F2] pt-14 pb-16 relative overflow-hidden">
    {/* Subtle grid */}
    <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
      style={{backgroundImage: "linear-gradient(#F5C543 1px, transparent 1px), linear-gradient(90deg, #F5C543 1px, transparent 1px)", backgroundSize: "48px 48px"}} />
    <div className="max-w-7xl mx-auto px-6 relative">
      {/* Top bar mimicking a real dashboard */}
      <div className="flex items-center justify-between text-[11px] font-mono mb-10 pb-4 border-b border-white/10">
        <div className="flex items-center gap-4">
          <span className="tracking-[0.24em] uppercase text-[#F5C543]">BuzzerAI · Voice AI</span>
          <span className="text-[#5C6B61]">v1.4 · region us-east</span>
        </div>
        <TickerStrip dark />
      </div>

      <div className="grid md:grid-cols-12 gap-8 items-start">
        <div className="md:col-span-7">
          <h1 className="font-heading text-5xl md:text-[80px] leading-[0.98] tracking-[-0.03em] text-white">
            Never miss<br/>
            <span className="text-[#F5C543]">a table.</span>
          </h1>
          <p className="mt-6 text-lg text-[#C2D1C1] max-w-md leading-snug">
            Mimi answers your restaurant's phone on ring 2. Waitlist joins itself. You keep seating.
          </p>
          <div className="mt-8 flex items-center gap-3">
            <Link to="/signup" className="inline-flex items-center gap-2 bg-[#F5C543] text-[#121914] text-sm font-bold px-5 py-3 rounded-md hover:bg-[#F7D470] transition-all">
              Start free <ArrowRight size={16} weight="bold" />
            </Link>
            <button className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white border border-white/15 px-4 py-3 rounded-md">
              <Waveform size={16} /> Hear Mimi
            </button>
            <span className="text-[10px] font-mono text-[#5C6B61] ml-2">From $29/mo · no contract</span>
          </div>
          {/* Live metrics row */}
          <div className="mt-10 grid grid-cols-4 gap-0 border-y border-white/10">
            {[
              ["Calls answered · today", "1,284", "#F5C543"],
              ["Avg pickup",             "1.9s",  "#C2D1C1"],
              ["Rings before Mimi",      "2",     "#C2D1C1"],
              ["Guests routed",          "312",   "#F5C543"],
            ].map(([k,v,c],i) => (
              <div key={k} className={`py-4 ${i < 3 ? "border-r border-white/10" : ""} px-4`}>
                <div className="text-[10px] tracking-widest uppercase text-[#5C6B61]">{k}</div>
                <div className="font-mono text-2xl mt-1" style={{color: c}}>{v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — compact live console */}
        <div className="md:col-span-5">
          <div className="rounded-lg border border-white/10 bg-[#141C19] overflow-hidden font-mono text-[12px]">
            <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-[#0E1512]">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#D96C5B]" />
                <span className="w-2 h-2 rounded-full bg-[#F5C543]" />
                <span className="w-2 h-2 rounded-full bg-[#C2D1C1]" />
                <span className="ml-3 text-[10px] text-[#5C6B61] tracking-widest uppercase">mimi.console · live</span>
              </div>
              <span className="text-[10px] text-[#F5C543]">● REC</span>
            </div>
            <div className="p-4 space-y-1.5">
              <div><span className="text-[#5C6B61]">→</span> <span className="text-[#F5C543]">INCOMING</span> · +1 415 •••• · 20:14:03</div>
              <div><span className="text-[#5C6B61]">→</span> <span className="text-[#C2D1C1]">RING 1</span> · owner mobile · no answer 4.2s</div>
              <div><span className="text-[#5C6B61]">→</span> <span className="text-[#C2D1C1]">RING 2</span> · handoff → Mimi</div>
              <div><span className="text-[#5C6B61]">→</span> <span className="text-white">"Mario's — this is Mimi, how can I help?"</span></div>
              <div><span className="text-[#5C6B61]">←</span> <span className="text-[#C2D1C1]">"Party of 4, table tonight?"</span></div>
              <div><span className="text-[#5C6B61]">→</span> <span className="text-white">"Yes — current wait is 22 min for four. Want a text when it's ready?"</span></div>
              <div><span className="text-[#5C6B61]">←</span> <span className="text-[#C2D1C1]">"Yeah, Chen."</span></div>
              <div><span className="text-[#5C6B61]">→</span> <span className="text-[#F5C543]">ADDED</span> · Chen · 4 · queued · sms armed</div>
              <div><span className="text-[#5C6B61]">→</span> <span className="text-[#F5C543]">LATENCY</span> · 18.2s total · $0.06 cost</div>
              <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                <CircleNotch size={12} className="animate-spin text-[#F5C543]" />
                <span className="text-[#5C6B61]">listening for next call…</span>
              </div>
            </div>
          </div>
          <div className="mt-3 text-[10px] font-mono text-[#5C6B61] flex items-center gap-1.5">
            <LiveDot /> Real transcript · your dashboard, minus the redactions.
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════ E — SIGNAL PULSE (Vercel × Cash App) ═══════════════════════ */
const HeroE = () => (
  <section id="opt-e" data-testid="hero-opt-e" className="bg-[#F7F6F2] pt-14 pb-16 relative overflow-hidden">
    {/* Diagonal accent */}
    <div className="absolute -right-40 -top-40 w-[600px] h-[600px] rounded-full bg-[#F5C543]/10 blur-3xl pointer-events-none" />
    <div className="max-w-7xl mx-auto px-6 relative">
      <div className="flex items-center gap-3 mb-6">
        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-[#1E3A2F] text-white text-[10px] font-bold tracking-widest uppercase">
          <LiveDot color="#F5C543" /> Ring 2
        </div>
        <TickerStrip />
      </div>

      <div className="grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <h1 className="font-heading text-6xl md:text-[104px] leading-[0.9] tracking-[-0.035em] text-[#0E1512]">
            Answer the<br/>
            phone. <span className="italic text-[#1E3A2F]">Faster</span><br/>
            than <span className="relative">
              <span className="relative z-10">humanly</span>
              <span className="absolute -bottom-1 left-0 right-0 h-4 bg-[#F5C543] -z-0" />
            </span> possible.
          </h1>
          <p className="mt-8 text-base text-[#5C6B61] max-w-md leading-snug">
            One number. AI receptionist. Live waitlist. Text-back-when-ready. <b className="text-[#121914]">From $29/mo, no contract.</b>
          </p>
          <div className="mt-8 flex items-center gap-3">
            <Link to="/signup" className="group inline-flex items-center gap-2 bg-[#1E3A2F] text-white text-sm font-semibold px-5 py-3 rounded-md hover:bg-[#0E1512] transition-all">
              Start free
              <ArrowRight size={16} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <button className="inline-flex items-center gap-2 text-sm text-[#121914] border border-[#121914]/15 hover:border-[#121914] px-4 py-3 rounded-md">
              <Lightning size={16} weight="fill" color="#F5C543" /> See it live
            </button>
          </div>
          {/* Waveform strip */}
          <div className="mt-10 flex items-end gap-1 h-10">
            {[8,14,22,10,28,16,32,20,12,26,18,34,14,20,28,10,22,16,30,18,26,12,20,8,24,14,28,16,10,22].map((h,i) => (
              <span key={`wave-${i}-${h}`} className="w-1.5 rounded-sm bg-[#1E3A2F]" style={{height: `${h}px`, opacity: 0.4 + (h/40)}} />
            ))}
            <span className="ml-3 text-[10px] font-mono text-[#5C6B61] tracking-widest uppercase self-end">Mimi · 1.9s pickup avg</span>
          </div>
        </div>

        {/* Right — real dashboard cards, compact */}
        <div className="md:col-span-5 space-y-3">
          {/* Compact incoming-call card */}
          <div className="bg-white rounded-lg ring-1 ring-black/5 shadow-[0_10px_40px_rgba(30,58,47,0.06)] p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md bg-[#1E3A2F] flex items-center justify-center">
                  <Phone size={14} color="#F5C543" weight="fill" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-[#121914]">Incoming · +1 415 •••</div>
                  <div className="text-[10px] text-[#5C6B61] font-mono flex items-center gap-1"><LiveDot color="#D96C5B" /> Mimi answering · 0:18</div>
                </div>
              </div>
              <span className="text-[10px] font-bold tracking-widest text-[#D96C5B]">LIVE</span>
            </div>
            <div className="text-xs text-[#121914] italic border-l-2 border-[#F5C543] pl-2">"Yes — 22 min for four. I'll text you Chen at your number ending 4471."</div>
          </div>
          {/* Compact waitlist row */}
          <div className="bg-white rounded-lg ring-1 ring-black/5 p-3">
            <div className="text-[10px] tracking-widest uppercase text-[#5C6B61] font-semibold mb-2">Waitlist · 6 parties · avg wait 24m</div>
            {[
              ["Chen",    "4", "queued",  "22m"],
              ["Alvarez", "2", "notify",  "notified"],
              ["Kim",     "6", "seated",  "T-12"],
            ].map(([n,p,s,t]) => (
              <div key={n} className="flex items-center justify-between py-1.5 border-t border-black/5 first:border-t-0">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#E6E2D6] text-[10px] font-bold flex items-center justify-center text-[#121914]">{p}</span>
                  <span className="text-xs text-[#121914]">{n}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${statusStyle(s)}`}>{s.toUpperCase()}</span>
                  <span className="text-[10px] font-mono text-[#5C6B61]">{t}</span>
                </div>
              </div>
            ))}
          </div>
          {/* Compact revenue proof card */}
          <div className="bg-[#1E3A2F] text-white rounded-lg p-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-widest uppercase text-[#F5C543]">Recovered · past 7 days</div>
              <div className="font-heading text-2xl mt-0.5">$4,180</div>
            </div>
            <ArrowUpRight size={20} color="#F5C543" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════ F — HEX BUZZ (subtle beehive DNA, product-forward) ═══════════════════════ */
const HeroF = () => (
  <section id="opt-f" data-testid="hero-opt-f" className="bg-[#FAFAFA] pt-14 pb-16 relative overflow-hidden">
    {/* Hex micro-pattern */}
    <svg className="absolute inset-0 w-full h-full opacity-[0.05] pointer-events-none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="hex" width="28" height="32" patternUnits="userSpaceOnUse" patternTransform="translate(0,0)">
          <polygon points="14,0 28,8 28,24 14,32 0,24 0,8" fill="none" stroke="#1E3A2F" strokeWidth="1"/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#hex)"/>
    </svg>

    <div className="max-w-7xl mx-auto px-6 relative">
      <div className="flex items-center justify-between mb-8">
        <div className="inline-flex items-center gap-2 text-[11px] font-mono tracking-widest uppercase text-[#5C6B61]">
          <LiveDot color="#F5C543" /> Buzzer · restaurant os
        </div>
        <TickerStrip />
      </div>

      <div className="grid md:grid-cols-12 gap-8 items-start">
        <div className="md:col-span-6">
          <h1 className="font-heading text-6xl md:text-[88px] leading-[0.95] tracking-[-0.035em] text-[#0E1512]">
            The <span className="text-[#D96C5B]">buzz</span><br/>
            answers<br/>
            <span className="text-[#1E3A2F] italic">the phone.</span>
          </h1>
          <p className="mt-6 text-base text-[#5C6B61] max-w-md leading-snug">
            AI receptionist. Living waitlist. Walk-in seating. One dashboard. Live in 4 minutes.
          </p>
          <div className="mt-8 flex items-center gap-3">
            <Link to="/signup" className="group relative inline-flex items-center gap-2 bg-[#0E1512] text-white text-sm font-bold px-6 py-3.5 rounded-full hover:bg-[#1E3A2F] transition-all">
              <span className="absolute inset-0 rounded-full ring-2 ring-[#F5C543]/40 animate-pulse pointer-events-none" />
              Start free
              <ArrowRight size={16} weight="bold" />
            </Link>
            <span className="text-xs text-[#5C6B61]">From $29/mo · no card · cancel in one tap</span>
          </div>
          {/* Compact honeycomb stat row */}
          <div className="mt-10 flex gap-2">
            {[
              ["43%",  "unanswered"],
              ["$126K","lost/yr"],
              ["2s",   "ring 2 pickup"],
              ["11",   "languages"],
            ].map(([n,l]) => (
              <div key={l} className="flex-1 bg-white ring-1 ring-black/5 rounded-md py-3 px-2.5">
                <div className="font-heading text-xl text-[#1E3A2F] leading-none">{n}</div>
                <div className="text-[10px] text-[#5C6B61] mt-1 leading-tight">{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — stacked, tilted mini dashboards */}
        <div className="md:col-span-6 relative min-h-[420px]">
          {/* Card 1 — Waitlist */}
          <div className="absolute top-0 right-0 w-[92%] bg-white rounded-xl ring-1 ring-black/5 shadow-[0_20px_50px_rgba(30,58,47,0.08)] p-4 rotate-[1.5deg]">
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-black/5">
              <div className="flex items-center gap-2">
                <Users size={14} color="#1E3A2F" weight="fill" />
                <span className="text-xs font-bold text-[#121914]">Waitlist</span>
                <span className="text-[10px] font-mono text-[#5C6B61]">6 parties</span>
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-[#5C6B61]"><LiveDot color="#F5C543" /> Master · OPEN</div>
            </div>
            <div className="space-y-1.5">
              {[
                ["Chen",    "4", "queued", "22m"],
                ["Alvarez", "2", "notify", "text sent"],
                ["Patel",   "3", "queued", "36m"],
              ].map(([n,p,s,t]) => (
                <div key={n} className="grid grid-cols-[24px_1fr_auto_auto] items-center gap-2 py-1 text-xs">
                  <span className="w-6 h-6 rounded-full bg-[#E6E2D6] text-[10px] font-bold flex items-center justify-center text-[#121914]">{p}</span>
                  <span className="text-[#121914]">{n}</span>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${statusStyle(s)}`}>{s.toUpperCase()}</span>
                  <span className="text-[10px] font-mono text-[#5C6B61] w-14 text-right">{t}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Card 2 — Live call, floats bottom-left */}
          <div className="absolute bottom-0 left-0 w-[70%] bg-[#0E1512] text-white rounded-xl p-4 -rotate-[2deg] shadow-[0_20px_50px_rgba(30,58,47,0.15)]">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md bg-[#F5C543] flex items-center justify-center">
                  <Phone size={14} color="#0E1512" weight="fill" />
                </div>
                <div>
                  <div className="text-[11px] font-semibold">Mimi · answering</div>
                  <div className="text-[9px] font-mono text-[#C2D1C1]">Ring 2 · 0:18</div>
                </div>
              </div>
              <span className="text-[9px] font-bold tracking-widest text-[#F5C543]">LIVE</span>
            </div>
            <div className="text-[11px] text-[#C2D1C1] leading-snug border-l border-[#F5C543] pl-2">
              "22 min for four. Texting you when Table 4 opens."
            </div>
          </div>
          {/* Card 3 — Revenue chip */}
          <div className="absolute top-[38%] right-[-8px] bg-[#F5C543] rounded-lg px-3 py-2 shadow-[0_10px_30px_rgba(245,197,67,0.4)] rotate-[6deg]">
            <div className="text-[9px] font-bold tracking-widest text-[#0E1512]">RECOVERED</div>
            <div className="font-heading text-xl text-[#0E1512] leading-none">+$4,180</div>
            <div className="text-[9px] text-[#0E1512]/70 mt-0.5">this week · vs. missed</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════ PAGE SHELL ═══════════════════════ */
export default function DesignPreview() {
  return (
    <div className="min-h-screen bg-white">
      {/* Sticky preview bar */}
      <div className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#0E1512] flex items-center justify-center">
              <BuzzLogo size={20} />
            </div>
            <div>
              <div className="font-heading text-base leading-none">BuzzerAI · Design Preview</div>
              <div className="text-[10px] text-[#5C6B61] tracking-widest uppercase mt-1">Round 2 · sharper, denser, product-forward</div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-1 text-xs">
            <a href="#opt-d" className="px-3 py-1.5 rounded-full hover:bg-black/5 transition-colors">D · Terminal</a>
            <a href="#opt-e" className="px-3 py-1.5 rounded-full hover:bg-black/5 transition-colors">E · Signal</a>
            <a href="#opt-f" className="px-3 py-1.5 rounded-full hover:bg-black/5 transition-colors">F · Hex Buzz</a>
            <Link to="/" className="ml-3 px-4 py-1.5 rounded-full bg-[#0E1512] text-white hover:bg-[#1E3A2F] transition-colors">Back to live</Link>
          </div>
        </div>
      </div>

      <Label n="D" name="Live Terminal" sub="Dark, dense, developer-serious. Real call transcript on the right. Live metric bar. Feels like the software is running in front of you." />
      <HeroD />

      <Label n="E" name="Signal Pulse" sub="Off-white, punchy oversized headline with a honey highlight, live incoming-call card + compact waitlist stack on the right. Fastest to scan, hardest CTA." />
      <HeroE />

      <Label n="F" name="Hex Buzz" sub="Owns the 'buzz' in the name — subtle honeycomb pattern, tilted stacked product cards, floating +$4,180 revenue chip. Most brand-forward and playful." />
      <HeroF />

      <section className="max-w-7xl mx-auto px-6 py-14 border-t border-black/10">
        <div className="font-heading text-2xl mb-2">Sharper. Denser. Alive.</div>
        <p className="text-sm text-[#5C6B61] max-w-2xl leading-relaxed">
          All three drop the stock restaurant photo, use tight 4–6 word headlines, and put the product in the hero — either as a terminal, a live call card, or floating dashboard tiles. Every option has a live ticker that ticks in real time. Pick D, E, or F — or say "hybrid: D headline + E right rail" and I'll build it.
        </p>
      </section>
    </div>
  );
}
